import { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

interface Permission {
  id: number;
  name: string;
  description?: string;
  category?: string;
}

interface PermissionCheckboxGroupProps {
  permissions: Permission[];
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
}

/**
 * PermissionCheckboxGroup - Grouped permission checkboxes with select all
 * Groups permissions by category with expand/collapse functionality
 */
export const PermissionCheckboxGroup = ({
  permissions,
  selectedIds,
  onChange,
}: PermissionCheckboxGroupProps) => {
  // Group permissions by category
  const groupedPermissions = permissions.reduce(
    (acc, permission) => {
      const category = permission.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(permission);
      return acc;
    },
    {} as Record<string, Permission[]>
  );

  const categories = Object.keys(groupedPermissions).sort();

  const handleToggle = (permissionId: number) => {
    const newSelectedIds = selectedIds.includes(permissionId)
      ? selectedIds.filter((id) => id !== permissionId)
      : [...selectedIds, permissionId];
    onChange(newSelectedIds);
  };

  const handleSelectAll = (category: string) => {
    const categoryPermissionIds = groupedPermissions[category].map((p) => p.id);
    const allSelected = categoryPermissionIds.every((id) =>
      selectedIds.includes(id)
    );

    if (allSelected) {
      // Deselect all in category
      onChange(selectedIds.filter((id) => !categoryPermissionIds.includes(id)));
    } else {
      // Select all in category
      const newSelectedIds = [
        ...selectedIds,
        ...categoryPermissionIds.filter((id) => !selectedIds.includes(id)),
      ];
      onChange(newSelectedIds);
    }
  };

  const isCategoryFullySelected = (category: string) => {
    const categoryPermissionIds = groupedPermissions[category].map((p) => p.id);
    return categoryPermissionIds.every((id) => selectedIds.includes(id));
  };

  return (
    <Box>
      {categories.map((category) => {
        const categoryPerms = groupedPermissions[category];
        const isFullySelected = isCategoryFullySelected(category);

        return (
          <Accordion key={category} defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${category}-content`}
              id={`${category}-header`}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {category.charAt(0).toUpperCase() + category.slice(1)} (
                  {categoryPerms.length})
                </Typography>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAll(category);
                  }}
                  sx={{ mr: 2 }}
                >
                  {isFullySelected ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {categoryPerms.map((permission) => (
                  <Box key={permission.id} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedIds.includes(permission.id)}
                          onChange={() => handleToggle(permission.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">
                            {permission.name}
                          </Typography>
                          {permission.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {permission.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
